import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Skeleton, Pagination } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import ListItem from '../../holders_list/components/MemberCard';
import { Homeless, Caseload, CareCoordinatorReferral, User, IncomingCCReferral } from 'types';
// API imports
import * as homelessesApi from '../../../api/homelesses';
import SamaritanFetch from '../../../api/httpClient';
import PopupReferral from '../../holders_list/components/ReferralComponents/PopupReferral';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            width: '96%',
            margin: '16px 0'
        },
        empty: {
            fontSize: '1.2rem',
            fontWeight: 600,
            marginLeft: '16px',
            textAlign: 'center',
            color: '#666'
        },
        skeletonContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '96%'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '32px',
            marginBottom: '8px',
        }
    })
);



//getting user's active caseloads (reverse to show most recent first)
const fetchMemberCaseloads = async (): Promise<Caseload[]> => {
    try {
        const response = await homelessesApi.fetchUserCaseloads();
        if (response && response.ok) {
            const data = await response.json();
            return data.ctp_caseloads.filter((caseload: Caseload) => caseload.status === 'active' ||  caseload.status === 'pending') || [];
        }
        throw new Error('Failed to fetch caseloads');
    } catch (error) {
        console.error('Error fetching caseloads:', error);
        throw error;
    }
};

//getting member by id
const fetchMemberById = async (memberId: number): Promise<Homeless | null> => {
    try {
        const response = await SamaritanFetch.v3(`/samaritan_members/${memberId}`, {
            query: { template: 'small' }
        });
        if (response && response.ok) {
            const data = await response.json();
            return data.samaritan_member || null;
        }
        return null;
    } catch (error) {
        console.error(`Error fetching member ${memberId}:`, error);
        return null;
    }
};

//getting members for current page
const fetchMembersForPage = async (caseloads: Caseload[], page: number, itemsPerPage: number): Promise<Homeless[]> => {
    try {
        // Calculate which caseloads are on this page
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pagesCaseloads = caseloads.slice(startIndex, endIndex);

        // Fetch only the members for this page
        const memberPromises = pagesCaseloads.map((caseload) => 
            fetchMemberById(caseload.homeless_id)
        );

        const members = await Promise.all(memberPromises);
        
        // Filter out null results only
        return members.filter((member): member is Homeless => member !== null);
    } catch (error) {
        console.error('Error fetching members for page:', error);
        throw error;
    }
};


type MyMemberListProps = {
    incomingCareCoordinatorReferrals: CareCoordinatorReferral[];
    fetchIncomingCareCoordinatorReferrals: (userID: number) => void;
    isFetchingIncomingCareCoordinatorReferrals: boolean;
    is_care_coordinator?: boolean;
    user: User;
    saveMemberToMyMembers: (caseloadID: number, memberID: number) => void;
    removeMemberFromMyMembers: (caseloadID: number, rejected?: boolean) => void;
};

const MyMemberList: React.FC<MyMemberListProps> = ({
    incomingCareCoordinatorReferrals,
    fetchIncomingCareCoordinatorReferrals,
    isFetchingIncomingCareCoordinatorReferrals,
    is_care_coordinator,
    user,
    saveMemberToMyMembers,
    removeMemberFromMyMembers
}) => {
    const classes = useStyles();
    
    const [members, setMembers] = useState<Homeless[]>([]);
    const [caseloads, setCaseloads] = useState<Caseload[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isMembersLoading, setIsMembersLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15); // Same as GroupMemberList

    const [referCaseload, setReferCaseload] = useState<Caseload | null>(
        null
    );
    const [referHolder, setReferHolder] = useState<Homeless | null>(null);
    const [referPopup, setReferPopup] = useState(false);

    const incomingReferralToCaseload = (
        ref: IncomingCCReferral
    ): Caseload => {
        return {
            id: ref.id,
            homeless_id: ref.homeless_id,
            status: "cc_referral",
            referral_ids: [],
            user_id: ref.referee_id,
            created_at: ref.created_at,
            updated_at: ref.updated_at,
            latest_referral: undefined,
            is_accepted: undefined,
        };
    };

    const loadBaseCaseloads = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
    
            const caseloadsData = await fetchMemberCaseloads();
            return caseloadsData;
        } catch (err) {
            setError("Failed to load caseloads");
            console.error(err);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, []);

    const combineCaseloads = useCallback(
        (incoming: IncomingCCReferral[], caseloadsData: Caseload[]): Caseload[] => {
            // CCReferralMembers (if CC user and cc referral active)
            // Pending Caseloads (with referred to you tag)
            // Active Caseloads
            const STATUS_ORDER: Record<string, number> = {
                cc_referral: 1,
                pending: 2,
                active: 3,
            };        
        
            const mappedIncoming = incoming.map(incomingReferralToCaseload);
        
            return [...mappedIncoming, ...caseloadsData].sort((a, b) => {
                const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
                if (statusDiff !== 0) return statusDiff;
            
                return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
            });
        }, []
    );
    useEffect(() => {
        if (is_care_coordinator) {
            fetchIncomingCareCoordinatorReferrals(user.id);
        }
    }, [is_care_coordinator, fetchIncomingCareCoordinatorReferrals, user.id]);

    useEffect(() => {
        const loadCaseloads = async () => {
            try {
                setIsLoading(true);
                setError(null);
    
                const caseloadsData = await loadBaseCaseloads();
    
                const incoming: CareCoordinatorReferral[] = is_care_coordinator
                    ? (incomingCareCoordinatorReferrals ?? [])
                    : [];
    
                const combined = combineCaseloads(incoming, caseloadsData);
                setCaseloads(combined);
            } catch (err) {
                console.error(err);
                setError("Failed to load caseloads");
            } finally {
                setIsLoading(false);
            }
        };
    
        // for care coordinator wait until incoming referrals fully loaded
        if (is_care_coordinator) {
            if (!isFetchingIncomingCareCoordinatorReferrals) {
                loadCaseloads();
            }
            return;
        }
    
        if (!is_care_coordinator) {
            loadCaseloads();
        }
    
    }, [is_care_coordinator, isFetchingIncomingCareCoordinatorReferrals, incomingCareCoordinatorReferrals, combineCaseloads, loadBaseCaseloads]);
    
    // Load members for current page when page changes or caseloads are loaded
    useEffect(() => {
        if (caseloads.length === 0) return;

        const loadMembersForPage = async () => {
            try {
                setIsMembersLoading(true);
                setError(null);
                
                const membersData = await fetchMembersForPage(caseloads, currentPage, itemsPerPage);
                setMembers(membersData);
            } catch (err) {
                setError('Failed to load members');
                console.error('Error loading members for page:', err);
            } finally {
                setIsMembersLoading(false);
            }
        };

        loadMembersForPage();
    }, [caseloads, currentPage, itemsPerPage]);

    //skeleton layout
    const SkeletonLayout = () => {
        return (
            <Box sx={{ padding: 2, width: '96%' }}>
                <Grid container spacing={4}>
                    {[...Array(itemsPerPage)].map((_, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={index}>
                            <Skeleton
                                variant="rounded"
                                height={200}
                                animation="wave"
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    //getting pagination info
    const getPaginationInfo = () => {
        const totalItems = caseloads.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        return { totalItems, totalPages };
    };

    //handling page change
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    //rendering members
    const renderMembers = () => {
        //if no caseloads, return empty message
        if (caseloads.length === 0) {
            return (
                <p className={classes.empty}>
                    No members found
                </p>
            );
        }

        //if members are loading, return skeleton layout
        if (isMembersLoading) {
            return <SkeletonLayout />;
        }

        //rendering members
        return members.map((member) => {
            const memberCaseload = caseloads.find(
                (caseload) => caseload.homeless_id === member.id
            );

            const memberCareCoordinatorReferrals = incomingCareCoordinatorReferrals?.filter(
                (referral) => referral.homeless_id === member.id
            ) || null;

            return (
                <Grid item xs={12} sm={6} md={4} key={`member-${member.id}`} sx={{ height: 200 }}>
                    <ListItem
                        caseload={memberCaseload!}
                        holder={member}
                        memberCareCoordinatorReferrals={memberCareCoordinatorReferrals}
                        setReferCaseload={setReferCaseload}
                        setReferHolder={setReferHolder}
                        setReferPopup={setReferPopup}
                    />
                </Grid>
            );
        });
    };

    //if loading (if caseloads are loading), return skeleton layout
    if (isLoading) {
        return <SkeletonLayout />;
    }

    //if error, return error message
    if (error) {
        return (
            <p className={classes.empty}>
                {error}
            </p>
        );
    }

    //rendering members
    return (
        <div className={classes.root}>
            <Grid container spacing={2}>
                {renderMembers()}
            </Grid>
            
            {/* Pagination Controls */}
            {getPaginationInfo().totalPages > 1 && (
                <div className={classes.paginationContainer}>
                    <Pagination
                        count={getPaginationInfo().totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: '#1F2152',
                                '&:hover': {
                                    backgroundColor: '#E5E7EB'
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#7378E8',
                                    color: '#FFFFFF'
                                }
                            }
                        }}
                        size="medium"
                        showFirstButton={false}
                        showLastButton={false}
                    />
                </div>
            )}

            {referPopup && (
                <PopupReferral
                    handleCloseClick={() => setReferPopup(false)}
                    referCaseload={referCaseload!}
                    referHolder={referHolder!}
                    removeMemberFromMyMembers={removeMemberFromMyMembers}
                    saveMemberToMyMembers={saveMemberToMyMembers}
                />
            )}
        </div>
    );
};

export default MyMemberList;
